/* ==========================================================================
   HuggingMum — theme runtime
   Custom elements only; no framework, no build step.
   ========================================================================== */
(function () {
  'use strict';

  const $ = (sel, root) => (root || document).querySelector(sel);
  const $$ = (sel, root) => Array.from((root || document).querySelectorAll(sel));
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* --- money ------------------------------------------------------------- */
  const moneyFormat = (window.HM && window.HM.moneyFormat) || '£{{amount}}';

  function formatMoney(cents) {
    const value = (cents / 100).toFixed(moneyFormat.indexOf('no_decimals') > -1 ? 0 : 2);
    const parts = value.split('.');
    const withSeparators = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1,');
    const amount = parts[1] ? withSeparators + '.' + parts[1] : withSeparators;
    return moneyFormat.replace(/\{\{\s*\w+\s*\}\}/, amount);
  }
  window.HM = Object.assign(window.HM || {}, { formatMoney });

  /* --- toast ------------------------------------------------------------- */
  let toastEl, toastTimer;
  function toast(message) {
    if (!toastEl) {
      toastEl = document.createElement('div');
      toastEl.className = 'toast';
      toastEl.setAttribute('role', 'status');
      document.body.appendChild(toastEl);
    }
    toastEl.textContent = message;
    requestAnimationFrame(() => toastEl.classList.add('is-visible'));
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => toastEl.classList.remove('is-visible'), 3200);
  }
  window.HM.toast = toast;

  /* --- focus trap -------------------------------------------------------- */
  const FOCUSABLE = 'a[href],button:not([disabled]),input:not([disabled]),select,textarea,[tabindex]:not([tabindex="-1"])';

  function trapFocus(container, onEscape) {
    function onKey(event) {
      if (event.key === 'Escape') { onEscape(); return; }
      if (event.key !== 'Tab') return;
      const items = $$(FOCUSABLE, container).filter((el) => el.offsetParent !== null);
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    }
    container.addEventListener('keydown', onKey);
    return () => container.removeEventListener('keydown', onKey);
  }

  /* --- scroll lock ------------------------------------------------------- */
  let lockCount = 0;
  let savedScroll = 0;
  function lockScroll() {
    if (lockCount++ > 0) return;
    savedScroll = window.scrollY;
    document.body.style.position = 'fixed';
    document.body.style.top = -savedScroll + 'px';
    document.body.style.width = '100%';
  }
  function unlockScroll() {
    if (--lockCount > 0) return;
    lockCount = 0;
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';
    window.scrollTo(0, savedScroll);
  }

  /* --- overlay ----------------------------------------------------------- */
  let overlay;
  function getOverlay() {
    if (!overlay) {
      overlay = document.createElement('div');
      overlay.className = 'drawer-overlay';
      document.body.appendChild(overlay);
    }
    return overlay;
  }

  /* ======================================================================
     <nav-drawer> — the tabbed mobile menu that slides under the header
     ====================================================================== */
  class NavDrawer extends HTMLElement {
    connectedCallback() {
      this.panel = $('[data-nav-panel-wrap]', this) || this;
      this.toggles = $$('[data-nav-toggle]');
      this.toggles.forEach((btn) => btn.addEventListener('click', () => this.toggle()));

      $$('[role="tab"]', this).forEach((tab) => {
        tab.addEventListener('click', () => this.select(tab.dataset.tab));
      });
      this.select(this.dataset.defaultTab || '0');
    }

    select(id) {
      $$('[role="tab"]', this).forEach((tab) => {
        tab.setAttribute('aria-selected', String(tab.dataset.tab === id));
      });
      $$('[role="tabpanel"]', this).forEach((panel) => {
        panel.hidden = panel.dataset.tab !== id;
      });
    }

    toggle() {
      const open = this.hasAttribute('hidden');
      this.hidden = !open;
      this.toggles.forEach((btn) => btn.setAttribute('aria-expanded', String(open)));
    }
  }
  customElements.define('nav-drawer', NavDrawer);

  /* ======================================================================
     <scroll-rail> — horizontal rail with dots and arrows.
     Above 1024 the markup may switch to a grid; the nav hides itself in CSS.
     ====================================================================== */
  class ScrollRail extends HTMLElement {
    connectedCallback() {
      this.track = $('[data-rail]', this);
      if (!this.track) return;
      this.dots = $$('[data-rail-dot]', this);
      this.prev = $('[data-rail-prev]', this);
      this.next = $('[data-rail-next]', this);

      if (this.prev) this.prev.addEventListener('click', () => this.step(-1));
      if (this.next) this.next.addEventListener('click', () => this.step(1));

      this.onScroll = this.throttle(() => this.sync());
      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onScroll, { passive: true });

      this.dots.forEach((dot, i) => dot.addEventListener('click', () => this.goTo(i)));
      this.sync();
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this.onScroll);
    }

    throttle(fn) {
      let queued = false;
      return () => {
        if (queued) return;
        queued = true;
        requestAnimationFrame(() => { queued = false; fn(); });
      };
    }

    get items() { return Array.from(this.track.children); }

    get index() {
      const left = this.track.scrollLeft;
      let best = 0;
      let bestDelta = Infinity;
      this.items.forEach((item, i) => {
        const delta = Math.abs(item.offsetLeft - this.track.offsetLeft - left);
        if (delta < bestDelta) { bestDelta = delta; best = i; }
      });
      return best;
    }

    goTo(i) {
      const item = this.items[i];
      if (!item) return;
      this.track.scrollTo({
        left: item.offsetLeft - this.track.offsetLeft,
        behavior: reduceMotion ? 'auto' : 'smooth'
      });
    }

    step(direction) { this.goTo(Math.max(0, Math.min(this.items.length - 1, this.index + direction))); }

    sync() {
      const i = this.index;
      this.dots.forEach((dot, n) => dot.classList.toggle('is-active', n === i));
      const atStart = this.track.scrollLeft <= 2;
      const atEnd = this.track.scrollLeft + this.track.clientWidth >= this.track.scrollWidth - 2;
      if (this.prev) this.prev.disabled = atStart;
      if (this.next) this.next.disabled = atEnd;
    }
  }
  customElements.define('scroll-rail', ScrollRail);

  /* ======================================================================
     <marquee-bar> — the ticker, with a pause control
     ====================================================================== */
  class MarqueeBar extends HTMLElement {
    connectedCallback() {
      const button = $('[data-ticker-pause]', this);
      if (!button) return;
      button.addEventListener('click', () => {
        const paused = this.classList.toggle('is-paused');
        button.setAttribute('aria-label', paused ? 'Play' : 'Pause');
        button.setAttribute('aria-pressed', String(paused));
      });
    }
  }
  customElements.define('marquee-bar', MarqueeBar);

  /* ======================================================================
     <accordion-group>
     ====================================================================== */
  class AccordionGroup extends HTMLElement {
    connectedCallback() {
      this.single = this.dataset.single === 'true';
      $$('[data-acc-trigger]', this).forEach((trigger) => {
        trigger.addEventListener('click', () => this.toggle(trigger));
      });
    }

    toggle(trigger) {
      const panel = trigger.nextElementSibling;
      if (!panel) return;
      const willOpen = panel.hidden;

      if (this.single && willOpen) {
        $$('[data-acc-trigger]', this).forEach((other) => {
          if (other === trigger) return;
          const otherPanel = other.nextElementSibling;
          if (otherPanel) otherPanel.hidden = true;
          other.setAttribute('aria-expanded', 'false');
          const mark = $('[data-acc-mark]', other);
          if (mark) mark.textContent = '+';
        });
      }

      panel.hidden = !willOpen;
      trigger.setAttribute('aria-expanded', String(willOpen));
      const mark = $('[data-acc-mark]', trigger);
      if (mark) mark.textContent = willOpen ? '−' : '+';
    }
  }
  customElements.define('accordion-group', AccordionGroup);

  /* ======================================================================
     <chip-filter> — client-side filtering of a product grid by tag
     ====================================================================== */
  class ChipFilter extends HTMLElement {
    connectedCallback() {
      this.chips = $$('[data-chip]', this);
      this.targets = $$('[data-chip-item]', document.getElementById(this.dataset.target) || this);
      this.chips.forEach((chip) => chip.addEventListener('click', () => this.select(chip)));
    }

    select(chip) {
      const value = chip.dataset.chip;
      this.chips.forEach((other) => other.setAttribute('aria-selected', String(other === chip)));
      this.targets.forEach((item) => {
        const tags = (item.dataset.chipItem || '').split('|');
        item.hidden = value !== '*' && tags.indexOf(value) === -1;
      });
    }
  }
  customElements.define('chip-filter', ChipFilter);

  /* ======================================================================
     <qty-input>
     ====================================================================== */
  class QtyInput extends HTMLElement {
    connectedCallback() {
      this.input = $('input', this);
      if (!this.input) return;
      $$('[data-qty]', this).forEach((btn) => {
        btn.addEventListener('click', () => {
          const step = Number(btn.dataset.qty);
          const min = Number(this.input.min || 1);
          const max = Number(this.input.max || 99);
          const next = Math.max(min, Math.min(max, Number(this.input.value || min) + step));
          if (next === Number(this.input.value)) return;
          this.input.value = next;
          this.input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      });
    }
  }
  customElements.define('qty-input', QtyInput);

  /* ======================================================================
     Cart — drawer, add, change, remove
     ====================================================================== */
  const Cart = {
    async add(items) {
      const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.description || data.message || 'Could not add to cart');
      return data;
    },

    async change(line, quantity) {
      const response = await fetch(window.Shopify.routes.root + 'cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line, quantity })
      });
      return response.json();
    },

    async refresh() {
      const response = await fetch(window.Shopify.routes.root + '?section_id=cart-drawer');
      const text = await response.text();
      const parsed = new DOMParser().parseFromString(text, 'text/html');
      const fresh = $('cart-drawer', parsed);
      const current = $('cart-drawer');
      if (fresh && current) {
        current.innerHTML = fresh.innerHTML;
        current.rebind();
      }
      const count = fresh && fresh.dataset.count;
      if (count != null) {
        $$('[data-cart-count]').forEach((el) => {
          el.textContent = count;
          el.hidden = Number(count) === 0;
        });
      }
    }
  };
  window.HM.Cart = Cart;

  class CartDrawer extends HTMLElement {
    connectedCallback() {
      this.releaseFocus = null;
      $$('[data-cart-open]').forEach((btn) => {
        btn.addEventListener('click', (event) => { event.preventDefault(); this.open(); });
      });
      getOverlay().addEventListener('click', () => this.close());
      this.rebind();
    }

    rebind() {
      $$('[data-cart-close]', this).forEach((btn) => btn.addEventListener('click', () => this.close()));

      $$('[data-line-qty]', this).forEach((input) => {
        input.addEventListener('change', async () => {
          const line = Number(input.dataset.lineQty);
          this.setBusy(true);
          await Cart.change(line, Number(input.value));
          await Cart.refresh();
          this.setBusy(false);
        });
      });

      $$('[data-line-remove]', this).forEach((btn) => {
        btn.addEventListener('click', async (event) => {
          event.preventDefault();
          this.setBusy(true);
          await Cart.change(Number(btn.dataset.lineRemove), 0);
          await Cart.refresh();
          this.setBusy(false);
        });
      });
    }

    setBusy(state) {
      const body = $('[data-cart-body]', this);
      if (body) body.style.opacity = state ? '.5' : '';
    }

    open() {
      const panel = $('[data-drawer-panel]', this);
      if (!panel) return;
      panel.classList.add('is-open');
      getOverlay().classList.add('is-open');
      lockScroll();
      this.releaseFocus = trapFocus(panel, () => this.close());
      const first = $(FOCUSABLE, panel);
      if (first) first.focus();
    }

    close() {
      const panel = $('[data-drawer-panel]', this);
      if (!panel || !panel.classList.contains('is-open')) return;
      panel.classList.remove('is-open');
      getOverlay().classList.remove('is-open');
      unlockScroll();
      if (this.releaseFocus) { this.releaseFocus(); this.releaseFocus = null; }
    }
  }
  customElements.define('cart-drawer', CartDrawer);

  /* ======================================================================
     <product-form> — add to cart without leaving the page
     ====================================================================== */
  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.form = $('form', this);
      if (!this.form) return;
      this.button = $('[type="submit"]', this.form);
      this.form.addEventListener('submit', (event) => this.submit(event));
    }

    async submit(event) {
      if (this.dataset.drawer !== 'true') return;
      event.preventDefault();
      const id = this.form.querySelector('[name="id"]');
      if (!id || !id.value) return;

      const original = this.button ? this.button.textContent : '';
      if (this.button) { this.button.disabled = true; this.button.textContent = 'Adding…'; }

      try {
        const quantity = Number((this.form.querySelector('[name="quantity"]') || {}).value || 1);
        const properties = {};
        $$('[name^="properties["]', this.form).forEach((field) => {
          const key = field.name.slice(11, -1);
          if (field.value) properties[key] = field.value;
        });
        await Cart.add([{ id: Number(id.value), quantity, properties }]);
        await Cart.refresh();
        const drawer = $('cart-drawer');
        if (drawer) drawer.open(); else toast('Added to cart');
      } catch (error) {
        toast(error.message);
      } finally {
        if (this.button) { this.button.disabled = false; this.button.textContent = original; }
      }
    }
  }
  customElements.define('product-form', ProductForm);

  /* ======================================================================
     <sticky-atc> — appears once the main buy box has scrolled away
     ====================================================================== */
  class StickyAtc extends HTMLElement {
    connectedCallback() {
      const anchor = document.getElementById(this.dataset.anchor || 'buybox');
      if (!anchor || !('IntersectionObserver' in window)) return;

      /* Publish the bar's height so the page can reserve room for it. Set once
         and left alone: tying it to the visible state would move the whole
         document at the exact moment the bar slides up, which is the one time
         the reader is already looking at it. */
      const setHeight = () => {
        const h = Math.round(this.getBoundingClientRect().height);
        document.documentElement.style.setProperty('--sticky-atc-h', h + 'px');
      };
      setHeight();

      const observer = new IntersectionObserver(
        ([entry]) => {
          const past = !entry.isIntersecting && entry.boundingClientRect.top < 0;
          this.classList.toggle('is-visible', past);
        },
        { rootMargin: '0px 0px -100% 0px', threshold: 0 }
      );
      observer.observe(anchor);

      if ('ResizeObserver' in window) new ResizeObserver(setHeight).observe(this);
    }
  }
  customElements.define('sticky-atc', StickyAtc);

  /* ======================================================================
     <predictive-search>
     ====================================================================== */
  class PredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.input = $('input[type="search"]', this);
      this.results = $('[data-search-results]', this);
      if (!this.input || !this.results) return;
      let timer;
      this.input.addEventListener('input', () => {
        clearTimeout(timer);
        timer = setTimeout(() => this.search(this.input.value.trim()), 260);
      });
    }

    async search(query) {
      if (query.length < 2) { this.results.innerHTML = ''; this.results.hidden = true; return; }
      const url = window.Shopify.routes.root +
        'search/suggest?q=' + encodeURIComponent(query) +
        '&resources[type]=product,collection,article&resources[limit]=6' +
        '&section_id=predictive-search';
      const response = await fetch(url);
      const text = await response.text();
      const parsed = new DOMParser().parseFromString(text, 'text/html');
      const fresh = $('[data-search-results]', parsed);
      this.results.innerHTML = fresh ? fresh.innerHTML : '';
      this.results.hidden = false;
    }
  }
  customElements.define('predictive-search', PredictiveSearch);

  /* ======================================================================
     Search drawer / generic drawer openers
     ====================================================================== */
  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-drawer-open]');
    if (opener) {
      event.preventDefault();
      const panel = document.getElementById(opener.dataset.drawerOpen);
      if (!panel) return;
      panel.classList.add('is-open');
      getOverlay().classList.add('is-open');
      lockScroll();
      const release = trapFocus(panel, close);
      const first = $(FOCUSABLE, panel);
      if (first) first.focus();
      function close() {
        panel.classList.remove('is-open');
        getOverlay().classList.remove('is-open');
        unlockScroll();
        release();
        getOverlay().removeEventListener('click', close);
      }
      getOverlay().addEventListener('click', close);
      $$('[data-drawer-close]', panel).forEach((btn) => btn.addEventListener('click', close, { once: true }));
    }
  });

  /* --- header height feeds the sticky nav offset -------------------------- */
  function syncHeaderHeight() {
    const header = $('[data-header]');
    if (header) document.documentElement.style.setProperty('--header-h', header.offsetHeight + 'px');
  }
  window.addEventListener('load', syncHeaderHeight);
  window.addEventListener('resize', syncHeaderHeight, { passive: true });
  syncHeaderHeight();
})();
